import React, { useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import EnhancedTable, {
  EnhancedTableColumn,
} from "fe-common/ui/table/enhancedTable";
import { format } from "date-fns";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { SupportedCommunicationPlatforms } from "fe-common/commonTypes/communicationLambdaApi";
import { LinearProgress, styled } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useClinicState } from "../../../../clinic/clinicStateContext";
import {
  InviteData,
  InvitePromiseData,
  InviteStatus,
  sendInvitation,
} from "../../util/sendInvitaion";
import { createClinicPatientCase } from "../createClinicPatientCase";
import { InviteStatusChip } from "./inviteStatusChip";
import { ClinicActionButton } from "fe-web-components/clinic/clinicActionButton";
import { SendingDialog } from "./sendingDialog";
import { localeSettings } from "../../clinicSettings/localeDefaults";
import { InviteForm } from "./inviteForm";
import { InviteDialog } from "./inviteDialog";

import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import ErrorIcon from "@mui/icons-material/Error";
import { ReactComponent as SuccessIcon } from "fe-web-components/assets/successIcon.svg";
import { isValidString } from "./common";

//region [[ Styles ]]
const useStyles = makeStyles({
  sendButton: {
    fontSize: 13,
    lineHeight: 1.2,
    minWidth: 146,
  },
  table: {
    "& .MuiTableContainer-root": {
      maxHeight: "calc(100vh - 270px)",
      width: "100%",
    },
    "& th": {
      position: "sticky",
      top: 0,
      zIndex: 100,
      background: "#fcfcfc",
    },
    "& .MuiTableCell-root": {
      padding: "6px 16px",
    },
    "& .MuiToolbar-regular": {
      paddingTop: 6,
      minHeight: "48px",
      background: "#fcfcfc",
    },
  },
  finalCountMessage: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
});
const InvitationsTableView = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 16,
});
const SendingCountView = styled("div")({
  display: "flex",
  marginBottom: 4,
  alignItems: "center",
  gap: 6,
});

const phoneNumberFormat = (
  phoneNumber: string,
  defaultCountryCode: string,
): string => {
  if (!phoneNumber) return "";
  if (defaultCountryCode) {
    const withPlus = "+" + defaultCountryCode;
    if (phoneNumber.startsWith(withPlus)) {
      phoneNumber = phoneNumber.replace(withPlus, "");
    }
  }

  return phoneNumber.replace(/(\d{3})(\d{3})(\d{3})/, "$1-$2-$3");
};
//endregion [[ Styles ]]

//region [[ Props ]]

//endregion [[ Props ]]

const dummyInvite: InviteData = {
  patient: {
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
  },
  visitDateTime: null,
  statusList: null,
  isNew: true,
};
//endregion [[ Functions ]]

export const InvitationsTable = () => {
  const [
    {
      clinicData: { name: clinicName, id: clinicId, displayName },
      defaultLocale,
    },
  ] = useClinicState();
  const defaultCountryCode = localeSettings[defaultLocale].countryCode;

  const tableColumns: EnhancedTableColumn[] = [
    {
      id: "fullName",
      name: "Full Name",
      enableSort: true,
      render: (rowData: InviteData) =>
        `${rowData.patient.firstName} ${rowData.patient.lastName}`,
    },
    {
      id: "visitTime",
      name: "Time of visit",
      enableSort: true,
      render: (rowData: InviteData) => {
        let dateString = rowData.visitDateTime || "";
        try {
          dateString = format(new Date(dateString), "Pp", {
            locale: localeSettings[defaultLocale].localeFormat,
          });
        } catch (e) {}
        return dateString;
      },
    },
    {
      id: "phone",
      name: "Phone",
      enableSort: true,
      render: (rowData: InviteData) =>
        phoneNumberFormat(rowData.patient.phoneNumber, defaultCountryCode),
    },
    {
      id: "email",
      name: "Email",
      enableSort: true,
      render: (rowData: InviteData) => rowData.patient.email,
    },
    {
      id: "status",
      name: "Status",
      enableSort: true,
      render: (rowData) => <InviteStatusChip invite={rowData} />,
    },
  ];

  const [tableData, setTableData] = useState<InviteData[]>(
    JSON.parse(sessionStorage.getItem("clinicInvites_" + clinicId)) || [],
  );
  const [showInviteDialog, setShowInviteDialog] = useState<InviteData>(null);
  const [showSendingDialog, setShowSendingDialog] = useState<boolean>(false);
  const [sending, setSending] = useState(false);
  const [errCountByMethod, setErrCountByMethod] = useState<
    Map<SupportedCommunicationPlatforms, number>
  >(new Map());
  const [sendMethods, setSendMethod] = useState<
    SupportedCommunicationPlatforms[]
  >([]);
  const [selected, setSelected] = useState<InviteData[]>([]);
  const [batchSentCountByMethod, setBatchSentCountByMethod] = useState<
    Map<SupportedCommunicationPlatforms, number>
  >(new Map());
  const [addingPatient, setAddingPatient] = useState<boolean>(false);

  const clearRef = useRef(false);
  //const unsent = tableData?.filter((i) => i.status !== InviteStatus.SentStatus);
  const classes = useStyles();

  useEffect(() => {
    if (tableData && clinicId) {
      // Update session storage
      sessionStorage.setItem(
        "clinicInvites_" + clinicId,
        JSON.stringify(tableData),
      );
    }
  }, [tableData, clinicId]);

  const handleDelete = (deleteInIndex: number[]) => {
    setTableData((prevState) =>
      prevState.filter((dataRow, index) => !deleteInIndex.includes(index)),
    );
  };

  const handleSave = async (inviteData: InviteData) => {
    if (inviteData.isNew) {
      setAddingPatient(true);
      const newCaseUrl = await createClinicPatientCase(clinicId, inviteData);
      //add row
      setTableData((prevState) => [
        ...prevState,
        {
          ...inviteData,
          isNew: false,
          id: uuid(),
          patient: { ...inviteData.patient, caseUrl: newCaseUrl.patient },
        },
      ]);
      setAddingPatient(false);
      return;
    }

    //TODO: update case as well
    //update row
    setTableData((prevState) => {
      Object.assign(
        prevState.find((row) => row.id === inviteData.id),
        { ...inviteData },
      );
      return [...prevState];
    });
  };

  const setInviteStatus = (
    invite: InviteData,
    method: SupportedCommunicationPlatforms,
    status: InviteStatus,
    msg?: string,
  ) => {
    setTableData((prevState) => {
      const inviteToUpdate = prevState.find((i) => i?.id === invite.id);
      if (!inviteToUpdate.statusList) inviteToUpdate.statusList = {};
      inviteToUpdate.statusList[method] = {
        status,
        msg,
      };

      return [...prevState];
    });
  };

  const handleSendErr = (
    invite: InviteData,
    method: SupportedCommunicationPlatforms,
    errMsg,
  ) => {
    setErrCountByMethod((prevState) => {
      const prevCount = prevState.get(method);
      prevState.set(method, prevCount ? prevCount + 1 : 1);
      return prevState;
    });
    setInviteStatus(
      invite,
      method,
      InviteStatus.ErrorStatus,
      `Error sending ${method}: ${errMsg}`,
    );
  };

  const handleSendSuccess = (
    invite: InviteData,
    method: SupportedCommunicationPlatforms,
  ) => {
    evt({
      category: CATEGORY.CLINIC_MANAGEMENT,
      action: ACTION.CLINIC_INVITE_SENT,
      props: {
        clinicName,
        invitationProperty: method,
        patientId: invite.patient.caseUrl,
        namedFilled:
          isValidString(invite?.patient?.firstName) ||
          isValidString(invite?.patient?.lastName),
        phoneFilled: isValidString(invite?.patient?.phoneNumber),
        emailFilled: isValidString(invite?.patient?.email),
        timeOfVisitFilled: !!invite?.visitDateTime,
      },
    });
    setBatchSentCountByMethod((prevState) => {
      const prevCount = prevState.get(method);
      prevState.set(method, prevCount ? prevCount + 1 : 1);
      return prevState;
    });
    setInviteStatus(
      invite,
      method,
      InviteStatus.SentStatus,
      `Successfully sent ${method}`,
    );
  };

  const handleSend = async () => {
    //reset counts
    setBatchSentCountByMethod((prevState) => {
      prevState.clear();
      return prevState;
    });
    setErrCountByMethod((prevState) => {
      prevState.clear();
      return prevState;
    });
    setSending(true);
    clearRef.current = false;

    const promises: Promise<InvitePromiseData>[] = sendMethods.flatMap(
      (sendMethod) =>
        selected.map((invite) => {
          setInviteStatus(invite, sendMethod, InviteStatus.SendingStatus);
          if (
            (sendMethod === SupportedCommunicationPlatforms.SMS ||
              sendMethod === SupportedCommunicationPlatforms.Whatsapp) &&
            !invite.patient.phoneNumber
          ) {
            handleSendErr(invite, sendMethod, "No phone number defined");
            return null;
          }
          if (
            sendMethod === SupportedCommunicationPlatforms.Email &&
            !invite.patient.email
          ) {
            handleSendErr(invite, sendMethod, "No email address defined");
            return null;
          }
          return sendInvitation({
            method: sendMethod,
            clinicName,
            clinicDisplayName: displayName,
            local: defaultLocale,
            inviteData: invite,
          })
            .then((response) => {
              if (response.success) handleSendSuccess(invite, sendMethod);
              else handleSendErr(invite, sendMethod, response.errMsg);
              return response;
            })
            .catch((e) => {
              handleSendErr(invite, sendMethod, e.message);
              return null;
            });
        }),
    );

    Promise.allSettled(promises).then((result) => {
      setTimeout(() => {
        clearRef.current = true; //clear all selections in table
        setSending(false);
        evt({
          category: CATEGORY.CLINIC_MANAGEMENT,
          action: ACTION.CLINIC_NUMBER_OF_INVITES,
          props: {
            clinicName,
            numberOfInvites: result.filter((r) => r?.status === "fulfilled")
              .length,
          },
        });
      }, 600);
      console.log("All sent:", result);
    });
  };

  return (
    <InvitationsTableView className="invitation-table">
      <EnhancedTable
        columns={tableColumns}
        data={tableData}
        className={classes.table}
        onDelete={handleDelete}
        onEdit={(rowData) => setShowInviteDialog(rowData)}
        onSelection={(rows) => {
          setSelected(rows);
        }}
        toolbarActions={
          <>
            <ClinicActionButton
              className={classes.sendButton}
              label={"ADD PATIENT"}
              endIcon={<AddIcon />}
              onClick={() => setShowInviteDialog(dummyInvite)}
              disabled={sending}
            />
          </>
        }
        noResultsMessage="To send an invitation please add a patient's contact info"
        disabled={sending}
        hidePagination={true}
        clearSelection={clearRef.current}
      />
      {!sending && (
        <ClinicActionButton
          onClick={() => setShowSendingDialog(true)}
          label={
            tableData.length
              ? selected.length === 0
                ? "No patients selected"
                : `Send ${selected.length} invitation${
                    selected.length > 0 ? "s" : ""
                  }`
              : "No patients defined"
          }
          endIcon={<SendIcon />}
          disabled={tableData.length === 0 || selected.length === 0}
        />
      )}
      {sending && (
        <div>
          <SendingCountView>
            <strong>Sending:</strong>
            {[...batchSentCountByMethod].map((methodCount, index) => (
              <span
                key={methodCount[0] + "_" + index}
              >{`Sent ${methodCount[0]} ${methodCount[1]} out of ${selected.length}`}</span>
            ))}
          </SendingCountView>
          <SendingProgress
            currentByMethod={batchSentCountByMethod}
            total={selected.length}
          />
        </div>
      )}
      {(batchSentCountByMethod.size > 0 || errCountByMethod.size > 0) && (
        <div>
          {sendMethods.map((method) => {
            const sentCount = batchSentCountByMethod.get(method);
            const errCount = errCountByMethod.get(method);
            return (
              <div key={method + "FinalStat"}>
                {sentCount > 0 && (
                  <span className={classes.finalCountMessage}>
                    <SuccessIcon />
                    {`${sentCount} invitation${
                      sentCount > 1 ? "s" : ""
                    } sent successfully by ${method}`}
                  </span>
                )}
                {errCount > 0 && (
                  <span className={classes.finalCountMessage}>
                    <ErrorIcon color={"error"} />
                    {`Failed to send ${errCount} invitation${
                      errCount > 1 ? "s" : ""
                    } by ${method}`}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
      {showInviteDialog && (
        <InviteDialog
          onClose={() => setShowInviteDialog(null)}
          title={showInviteDialog?.isNew ? "Add Patient" : "Edit Patient"}
        >
          <InviteForm
            inviteData={showInviteDialog}
            onSubmit={async (inviteData) => {
              await handleSave(inviteData);
              setShowInviteDialog(null);
            }}
            defaultLocale={defaultLocale}
            validateForm={
              (inviteFormData) =>
                !!inviteFormData.email || !!inviteFormData.phoneNumber //at least one contact method is required
            }
            saving={addingPatient}
          />
        </InviteDialog>
      )}
      {showSendingDialog && (
        <SendingDialog
          batchSize={selected.length}
          sendMethods={sendMethods}
          onClose={() => setShowSendingDialog(false)}
          onSetMethods={(methods) => setSendMethod(methods)}
          onSend={handleSend}
        />
      )}
    </InvitationsTableView>
  );
};

const SendingProgress = (props: {
  currentByMethod: Map<SupportedCommunicationPlatforms, number>;
  total: number;
}) => {
  let sum = 0;
  props.currentByMethod.forEach((v) => {
    sum += v;
  });
  return (
    <LinearProgress variant="determinate" value={(sum * 100) / props.total} />
  );
};
